import { DateTime } from 'luxon';
import type { AwardPaymentRequest } from '../../types/awardPayment';
import { ACTIVE_REVIEW_STATUSES, COMPLETED_STATUSES, normalizePaymentRequestStatus } from './paymentRequestStatuses';
import { dateToLuxonDate } from '../../util/dateUtil';
import { INCENTIVE, SCHOLARSHIP } from './paymentRequestTypes';

export const parseDateTime = (value: string): DateTime | null => {
  if (!value) return null;
  if (typeof value === 'string' && value.startsWith('/Date(')) {
    const dt = dateToLuxonDate(value);
    return dt && typeof dt !== 'string' && dt.isValid ? dt : null;
  }
  const dt = DateTime.fromJSDate(new Date(value)).toUTC();
  return dt.isValid ? dt : null;
};

export const getRequesterName = (awardPaymentRequest: AwardPaymentRequest) =>
  `${awardPaymentRequest.firstName ?? ''} ${awardPaymentRequest.lastName ?? ''}`.trim();

const getDaysFromTimestamps = (startTimestamp: number, endTimestamp: number): number => {
  const startDate = DateTime.fromMillis(startTimestamp).startOf('day');
  const endDate = DateTime.fromMillis(endTimestamp).startOf('day');
  return Math.max(0, Math.floor(endDate.diff(startDate, 'days').days));
};

export const getDaysOpen = ({
  awardPaymentRequest,
  submittedTimestamp,
}: {
  awardPaymentRequest: AwardPaymentRequest;
  submittedTimestamp: number;
}): number => {
  if (!submittedTimestamp || submittedTimestamp === 0) return 0;
  const normalizedStatus = normalizePaymentRequestStatus(awardPaymentRequest.status);
  const updatedTimestamp = parseDateTime(awardPaymentRequest.updatedAt)?.toMillis() ?? 0;
  const isBelongsToCompletedStatus = COMPLETED_STATUSES.has(normalizedStatus);
  if (isBelongsToCompletedStatus) {
    return getDaysFromTimestamps(submittedTimestamp, updatedTimestamp);
  }
  return getDaysFromTimestamps(submittedTimestamp, DateTime.now().toUTC().toMillis());
};

export const getActiveReviewStatusCounts = (awardPaymentRequests: AwardPaymentRequest[]) => {
  const counts: Record<string, number> = Array.from(ACTIVE_REVIEW_STATUSES).reduce((acc, s) => {
    acc[s] = 0;
    return acc;
  }, {} as Record<string, number>);

  awardPaymentRequests.forEach((awardPaymentRequest) => {
    const s = normalizePaymentRequestStatus(awardPaymentRequest.status);
    if (ACTIVE_REVIEW_STATUSES.has(s)) {
      counts[s] = (counts[s] ?? 0) + 1;
    }
  });

  return counts;
};

export const getAmountRequestedDisplay = (awardPaymentRequest: AwardPaymentRequest): string => {
  const amountText = String(awardPaymentRequest.amountRequested ?? '').trim();
  const amountValue = awardPaymentRequest.amountRequestedValue;
  const isMissingOrZeroAmount =
    amountValue == null || amountValue === 0 || amountText === '' || amountText === '0' || amountText === '$0' || amountText === '$0.00';

  if (awardPaymentRequest.type === INCENTIVE && isMissingOrZeroAmount) {
    return 'N/A';
  }

  if (awardPaymentRequest.type === SCHOLARSHIP && isMissingOrZeroAmount) {
    return '$0.00';
  }

  if (amountText !== '') {
    return amountText;
  }

  return amountValue == null ? '$0.00' : amountValue.toString();
};
